import { CheckCircleTwoTone, FileOutlined, FileSyncOutlined, MailOutlined, SyncOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Input, Layout, Menu, message, PageHeader, Popconfirm, Row, Space, Table, Tooltip } from "antd";
import { useApplicationContext } from "components/Application";
import { CenterSpinner } from "components/CenterSpinner";
import { replace } from "lodash";
import moment from "moment";
import React, { Fragment, useState } from "react";
import { NavLink } from "react-router-dom";

const orderDocumentsQuery = gql`
  query(
    $pagination: ConnectionPagination
    $sorting: OrderDocumentSorting
    $searchQuery: String
    $status: String
    $salespersonId: ID
  ) {
    orderDocuments(
      pagination: $pagination
      sorting: $sorting
      searchQuery: $searchQuery
      status: $status
      salespersonId: $salespersonId
    ) {
      nodes {
        id
        seasonKind
        updatedAt
        document
        magoSent
        magoCode
        confirmedOn
        quantity
        total
        user {
          id
          companyName
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const magoSendOrderDocumentMutation = gql`
  mutation($id: ID!) {
    magoSendOrderDocument(id: $id) {
      successful
    }
  }
`;

const renderOrderDocumentMutation = gql`
  mutation($id: ID!) {
    renderOrderDocument(id: $id) {
      document {
        id
        document
      }
    }
  }
`;

const mailingOrderDocumentMutation = gql`
  mutation($id: ID!) {
    mailingOrderDocument(id: $id) {
      document {
        id
      }
    }
  }
`;

export const List: React.FC<{}> = () => {
  const {
    siteEnvironment: { appEndpointStore },
    session,
  } = useApplicationContext();
  const [searchQuery, setSearchQuery] = useState<string>("");

  const salesperson_id = session.currentUser.role === "SALESPERSON" ? session.currentUser.id : "admin";

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "DESC", field: "INSERTED_AT" },
    status: "confirmed",
    salespersonId: salesperson_id,
    searchQuery,
  };
  const { loading, data } = useQuery(orderDocumentsQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const [execMutation] = useMutation(magoSendOrderDocumentMutation, {});

  const magoSyncOrder = (orderDocument: any) => {
    execMutation({ variables: { id: orderDocument.id } });
  };

  const [generatePdf] = useMutation(renderOrderDocumentMutation, {
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      message.success("PDF successfully generated");
    },
  });

  const [sendEmails] = useMutation(mailingOrderDocumentMutation, {
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      message.success("Emails successfully sent");
    },
  });

  if (loading) return <CenterSpinner size="large" />;

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const columns = [
    {
      title: "",
      render: (resource: any) => (
        (session.currentUser.role === "ADMIN") &&
        <>
          {resource.magoSent ? (
            <Tooltip title="Synced">
              <Button type="text" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} />
            </Tooltip>
          ) : (
            <Tooltip title="Force Mago Sync">
              <Button onClick={() => magoSyncOrder(resource)} danger type="link" icon={<SyncOutlined />} />
            </Tooltip>
          )}
        </>
      ),
      filters: [
        {
          text: 'SENT',
          value: true,
        },
        {
          text: 'ERROR',
          value: false,
        },
      ],
      onFilter: (value: boolean, record: any) => record.magoSent.toString().indexOf(value) === 0,
    },
    {
      title: "Order",
      render: (resource: any) => (
        <>
          {resource.seasonKind === "stock" ? "PR" : "ST"}
          {resource.magoCode !== "UNKNOWN" ? (
            <NavLink to={resource.id}> {replace(resource.magoCode, "SO-", "")}</NavLink>
          ) : (
            ""
          )}
        </>
      ),
      filters: [
        {
          text: 'SEASONAL',
          value: 'current',
        },
        {
          text: 'STOCK',
          value: 'stock',
        },
      ],
      onFilter: (value: any, record: any) => record.seasonKind.toString().indexOf(value) === 0,
    },
    {
      title: "Customer",
      render: (resource: any) => <NavLink to={resource.id}>{resource.user.companyName}</NavLink>,
      sorter: (a: any, b: any) => a.user.companyName.localeCompare(b.user.companyName),
    },
    {
      title: "Confirmed",
      render: (resource: any) => moment(resource.confirmedOn).format("DD-MM-YYYY"),
    },
    {
      title: "Total Qty",
      dataIndex: "quantity",
      sorter: (a: any, b: any) => a.quantity - b.quantity,
    },
    {
      title: "Total",
      dataIndex: "total",
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Row justify="end">
          <Space>
            {resource.document && (
              <Tooltip title="Download PDF" placement="bottom">
                <a target="_blank" rel="noreferrer" href={`${appEndpointStore}${resource.document}`}>
                  <Button icon={<FileOutlined />}></Button>
                </a>
              </Tooltip>
            )}
            {session.currentUser.role === 'ADMIN' && (
              <Tooltip title="Generate PDF" placement="bottom">
                <Button icon={<FileSyncOutlined />} onClick={() => generatePdf({ variables: { id } })}></Button>
              </Tooltip>
            )}
            {session.currentUser.role === 'ADMIN' && (
              <Popconfirm
                title="Are you sure to send emails again?"
                icon={null}
                onConfirm={() => sendEmails({ variables: { id } })}
              >
                <Tooltip title="Send Emails" placement="bottom">
                  <a target="_blank" rel="noreferrer" href={`${appEndpointStore}${resource.document}`}>
                    <Button icon={<MailOutlined />}></Button>
                  </a>
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        </Row>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
              />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader ghost={true} title="Orders" />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.orderDocuments.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              total: data?.totalCount ?? 0,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
